export const idlFactory = ({ IDL }) => {
  const Position = IDL.Record({ 'x' : IDL.Float32, 'y' : IDL.Float32 });
  const PlayerStance = IDL.Variant({
    'Defensive' : IDL.Null,
    'Offensive' : IDL.Null,
  });
  const Action = IDL.Variant({
    'Leave' : IDL.Null,
    'UseItem' : IDL.Record({ 'slot' : IDL.Nat8 }),
    'Guard' : IDL.Record({
      'to' : Position,
      'path' : IDL.Opt(IDL.Vec(Position)),
    }),
    'DropWeapon' : IDL.Null,
    'Move' : IDL.Record({
      'to' : Position,
      'path' : IDL.Opt(IDL.Vec(Position)),
    }),
    'Stop' : IDL.Null,
    'DropItemFromInventory' : IDL.Record({ 'slot' : IDL.Nat8 }),
    'UseItemAt' : IDL.Record({ 'at' : Position, 'slot' : IDL.Nat8 }),
    'ChangeStanceTo' : PlayerStance,
  });
  const ActionWithId = IDL.Record({
    'id' : IDL.Nat64,
    'action' : Action,
    'immediate' : IDL.Bool,
  });
  const GameEvent = IDL.Variant({
    'Kill' : IDL.Record({ 'killer' : IDL.Principal, 'victim' : IDL.Principal }),
    'PlayerLeave' : IDL.Record({ 'player' : IDL.Principal }),
    'Death' : IDL.Record({ 'player' : IDL.Principal, 'cause' : IDL.Text }),
  });
  const Modifier = IDL.Variant({
    'ExtraDamage' : IDL.Int32,
    'ExtraMoveSpeed' : IDL.Float32,
    'ExtraArmor' : IDL.Float32,
    'ExtraHealth' : IDL.Float32,
  });
  const ItemType = IDL.Variant({
    'MedKit' : IDL.Record({ 'count' : IDL.Nat8, 'health' : IDL.Int32 }),
    'Boots' : IDL.Record({
      'level' : IDL.Nat8,
      'speed' : IDL.Float32,
      'modifiers' : IDL.Vec(Modifier),
    }),
    'Knife' : IDL.Record({
      'damage' : IDL.Int32,
      'level' : IDL.Nat8,
      'fire_speed' : IDL.Float32,
      'modifiers' : IDL.Vec(Modifier),
      'range' : IDL.Float32,
    }),
    'Grenade' : IDL.Record({
      'damage' : IDL.Int32,
      'count' : IDL.Nat8,
      'explosion_radius' : IDL.Nat64,
      'range' : IDL.Float32,
    }),
    'EnergyDrink' : IDL.Record({
      'gametime_length' : IDL.Float32,
      'count' : IDL.Nat8,
      'fire_speed' : IDL.Float32,
      'move_speed' : IDL.Float32,
    }),
    'Armor' : IDL.Record({
      'armor' : IDL.Float32,
      'level' : IDL.Nat8,
      'modifiers' : IDL.Vec(Modifier),
    }),
    'Pistol' : IDL.Record({
      'damage' : IDL.Int32,
      'level' : IDL.Nat8,
      'fire_speed' : IDL.Float32,
      'modifiers' : IDL.Vec(Modifier),
      'range' : IDL.Float32,
    }),
  });
  const ItemWithId = IDL.Record({
    'id' : IDL.Nat32,
    'loc' : Position,
    'item_type' : ItemType,
  });
  const ItemEvent = IDL.Variant({
    'Improved' : IDL.Record({ 'player' : IDL.Principal }),
    'OnGround' : IDL.Null,
  });
  const GameObject = IDL.Variant({
    'Grenade' : IDL.Record({
      'loc' : Position,
      'damage' : IDL.Int32,
      'owner' : IDL.Principal,
      'explosion_radius' : IDL.Nat64,
      'speed' : IDL.Float32,
      'target' : Position,
    }),
  });
  const GameObjectWithId = IDL.Record({
    'id' : IDL.Nat32,
    'object_type' : GameObject,
  });
  const ObjectEvent = IDL.Variant({
    'GrenadeMoved' : IDL.Record({ 'to' : Position, 'from' : Position }),
    'Explosion' : IDL.Record({
      'center' : Position,
      'tiles' : IDL.Vec(IDL.Tuple(IDL.Nat64, IDL.Nat64)),
    }),
  });
  const Inventory = IDL.Record({
    'slots' : IDL.Vec(IDL.Opt(ItemWithId)),
    'weapon' : IDL.Opt(ItemWithId),
  });
  const TemporaryStatus = IDL.Variant({
    'Energized' : IDL.Record({
      'end_time' : IDL.Float32,
      'fire_speed' : IDL.Float32,
      'move_speed' : IDL.Float32,
    }),
  });
  const PlayerStatus = IDL.Record({
    'stance' : PlayerStance,
    'max_health' : IDL.Float32,
    'armor' : IDL.Float32,
    'inventory' : Inventory,
    'movement_speed' : IDL.Float32,
    'temporary_statuses' : IDL.Vec(TemporaryStatus),
    'attack_speed' : IDL.Float32,
    'weapon' : IDL.Opt(ItemWithId),
    'health' : IDL.Float32,
  });
  const PlayerEvent = IDL.Variant({
    'PlayerGuarding' : IDL.Record({ 'at' : Position, 'angle' : IDL.Float32 }),
    'PlayerDead' : IDL.Record({ 'at' : Position }),
    'PlayerIdle' : IDL.Record({ 'at' : Position, 'angle' : IDL.Float32 }),
    'PlayerHealthChange' : IDL.Record({
      'at' : Position,
      'health' : IDL.Float32,
    }),
    'PlayerAttacked' : IDL.Record({
      'at' : Position,
      'hit' : IDL.Bool,
      'from' : Position,
      'weapon' : ItemType,
    }),
    'PlayerMoved' : IDL.Record({ 'to' : Position, 'from' : Position }),
  });
  const PlayerEventData = IDL.Record({
    'status' : PlayerStatus,
    'player' : IDL.Principal,
    'queue' : IDL.Opt(IDL.Vec(ActionWithId)),
    'event' : PlayerEvent,
    'last_processed_action_id' : IDL.Nat64,
  });
  const EventData = IDL.Variant({
    'Game' : GameEvent,
    'Item' : IDL.Record({ 'item' : ItemWithId, 'event' : ItemEvent }),
    'Object' : IDL.Record({
      'object' : GameObjectWithId,
      'event' : ObjectEvent,
    }),
    'Player' : PlayerEventData,
  });
  const EventEntry = IDL.Record({
    'data' : EventData,
    'end_time' : IDL.Float32,
    'start_time' : IDL.Float32,
  });
  const EventsBatch = IDL.Record({
    'events' : IDL.Vec(EventEntry),
    'round' : IDL.Nat32,
  });
  const EventsResponse = IDL.Variant({
    'NotInGame' : IDL.Null,
    'Events' : IDL.Vec(EventsBatch),
    'EventError' : IDL.Text,
  });
  const MapTile = IDL.Variant({
    'Empty' : IDL.Null,
    'Wall' : IDL.Null,
    'Brush' : IDL.Record({ 'id' : IDL.Nat16 }),
    'HouseDoor' : IDL.Record({ 'id' : IDL.Nat16 }),
    'Indoors' : IDL.Record({ 'id' : IDL.Nat16 }),
    'Impassable' : IDL.Null,
  });
  const Object = IDL.Variant({
    'Rock' : IDL.Record({
      'loc' : Position,
      'height' : IDL.Nat8,
      'width' : IDL.Nat8,
    }),
    'Brush' : IDL.Record({ 'loc' : Position, 'radius' : IDL.Nat8 }),
    'House' : IDL.Record({
      'loc' : Position,
      'height' : IDL.Nat8,
      'door_x' : IDL.Nat8,
      'door_y' : IDL.Nat8,
      'width' : IDL.Nat8,
    }),
  });
  const Map = IDL.Record({
    'height' : IDL.Nat64,
    'tiles' : IDL.Vec(IDL.Vec(MapTile)),
    'objects' : IDL.Vec(Object),
    'items' : IDL.Vec(ItemWithId),
    'width' : IDL.Nat64,
  });
  const Player = IDL.Record({
    'loc' : Position,
    'stance' : PlayerStance,
    'max_health' : IDL.Float32,
    'is_practice_enemy' : IDL.Bool,
    'principal' : IDL.Principal,
    'angle' : IDL.Float32,
    'armor' : IDL.Float32,
    'left' : IDL.Bool,
    'inventory' : Inventory,
    'name' : IDL.Text,
    'movement_speed' : IDL.Float32,
    'temporary_statuses' : IDL.Vec(TemporaryStatus),
    'queue' : IDL.Vec(ActionWithId),
    'base_fire_speed' : IDL.Float32,
    'fire_speed' : IDL.Float32,
    'last_fire' : IDL.Float32,
    'kills' : IDL.Nat32,
    'base_movement_speed' : IDL.Float32,
    'last_processed_action_id' : IDL.Nat64,
    'base_health' : IDL.Float32,
    'health' : IDL.Float32,
  });
  const PublicPlayer = IDL.Record({
    'principal' : IDL.Principal,
    'dead' : IDL.Bool,
    'name' : IDL.Text,
    'kills' : IDL.Nat32,
  });
  const TutorialGameStage = IDL.Variant({
    'EnemyShowsItself' : IDL.Null,
    'EnemyIdle' : IDL.Null,
    'EnemyWalksAround' : IDL.Null,
  });
  const TutorialGame = IDL.Record({ 'stage' : TutorialGameStage });
  const GameType = IDL.Variant({
    'Regular' : IDL.Null,
    'Tutorial' : TutorialGame,
  });
  const PublicGame = IDL.Record({
    'map' : Map,
    'player' : Player,
    'players' : IDL.Vec(PublicPlayer),
    'events' : IDL.Vec(EventsBatch),
    'game_type' : GameType,
    'started_at_real_time' : IDL.Nat64,
    'round' : IDL.Nat32,
  });
  const ChatMessage = IDL.Record({
    'name' : IDL.Text,
    'message' : IDL.Text,
    'timestamp' : IDL.Nat64,
  });
  const LobbyPlayer = IDL.Record({
    'principal' : IDL.Principal,
    'name' : IDL.Text,
  });
  const PlayerState = IDL.Variant({
    'Outside' : IDL.Null,
    'InGame' : PublicGame,
    'InLobby' : IDL.Record({
      'chat' : IDL.Vec(ChatMessage),
      'players' : IDL.Vec(LobbyPlayer),
      'game_in_progress' : IDL.Bool,
    }),
  });
  return IDL.Service({
    'act' : IDL.Func([ActionWithId], [], []),
    'chat_lobby' : IDL.Func([IDL.Text], [], []),
    'get_events' : IDL.Func(
        [IDL.Opt(IDL.Nat32), IDL.Nat64],
        [EventsResponse],
        ['query'],
      ),
    'get_game' : IDL.Func([], [PublicGame], ['query']),
    'get_player_state' : IDL.Func([IDL.Nat64], [PlayerState], ['query']),
    'join_lobby' : IDL.Func([IDL.Text], [], []),
    'leave_lobby' : IDL.Func([], [], []),
    'start_game' : IDL.Func([], [], []),
    'start_tutorial_game' : IDL.Func([], [], []),
  });
};
export const init = ({ IDL }) => { return []; };
